import React, { useEffect, useState, useRef } from "react"
import { TableColumnsType, Spin, Row, Col, Typography, Tag, Modal, Input, message, Button } from "antd"
import { useParams, useHistory } from "react-router-dom"
import { RollbackOutlined, MailOutlined } from "@ant-design/icons"
import { getDocumentLabels, textNumberFormater, formatDateTime } from "../utils"
import { isExitMovement } from "../utils/reportUtils"
import { useCompany } from "../contexts/CompanyContext"
import { useConfig } from "../contexts/ConfigContext"
import { useValidateResourceCompany } from "../hooks/useValidateResourceCompany"
import { useSecureRequest } from "../hooks/useSecureRequest"
import { useStore } from "../contexts/StoreContext"
import { useRegister } from "../contexts/RegisterContext"
import { IMovementRS } from "../models/storageModel"

import FulfillMovementModal from "./FulfillMovementModal"
import RegisterMovementPaymentModal from "./RegisterMovementPaymentModal"
import MovementTimelineView from "./MovementTimelineView"

import MovementDetailHeaderBar from "./movement-detail/MovementDetailHeaderBar"
import MovementDetailItemsCard from "./movement-detail/MovementDetailItemsCard"
import MovementDetailAttachmentsCard from "./movement-detail/MovementDetailAttachmentsCard"
import MovementDetailNotesCard from "./movement-detail/MovementDetailNotesCard"
import MovementDetailDebtCard from "./movement-detail/MovementDetailDebtCard"
import MovementDetailElectronicInvoiceCard from "./movement-detail/MovementDetailElectronicInvoiceCard"
import MovementDetailGeneralInfoCard from "./movement-detail/MovementDetailGeneralInfoCard"

export interface Item {
  product: string;
  quantity: number;
  price: number;
  total: number;
  discount: number;
  taxAmount: number;
  baseAmount: number;
}

export interface Taxes {
  id: number;
  key: string;
  description: string;
  tax_amount: number;
}

export interface ReceiptData {
  receiptId: string;
  date: string;
  customer: string;
  seller: string;
  pdv: string;
  items: Item[];
  subtotal: number;
  tax: number;
  total: number;
  totalDiscount: number;
  companyName: string;
  companyAddress: string;
  taxesList: Taxes[];
  message?: string;
  documentTitle: string;
  counterpartyLabel: string;
  userLabel: string;
}

const { Title, Text } = Typography

function ViewMovementDetail() {
  const { id } = useParams<{ id: string }>()
  const history = useHistory()
  const { config } = useConfig()
  const { company } = useCompany()
  const { register } = useRegister()
  const { store, updateStore } = useStore() 
  const { securePost } = useSecureRequest()
  const { validateResourceCompany, isSwitchingCompany } = useValidateResourceCompany()

  const [movement, setMovement] = useState<Partial<IMovementRS> | null>(null)
  const [notes, setNotes] = useState<Partial<IMovementRS>[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [emailModalVisible, setEmailModalVisible] = useState(false)
  const [emailTarget, setEmailTarget] = useState("")
  const [sendingEmail, setSendingEmail] = useState(false)
  const [retrying, setRetrying] = useState(false)
  const [fulfillModalVisible, setFulfillModalVisible] = useState(false)
  const [paymentModalVisible, setPaymentModalVisible] = useState(false)
  const [timelineRefreshKey, setTimelineRefreshKey] = useState(0)

  const isFetchingRef = useRef(false)
  const lastFetchedKeyRef = useRef("")

  const fetchMovementData = async (force: boolean = false) => {
    const companyUid = company.current?.uid
    if (!id || !companyUid) return

    const requestKey = `${id}_${companyUid}`
    if (!force && lastFetchedKeyRef.current === requestKey) {
      return
    }

    if (isFetchingRef.current && !force) return
    isFetchingRef.current = true
    lastFetchedKeyRef.current = requestKey
    setLoading(true)

    try {
      // 1. Fetch storehouse only if not already cached
      if (!store.list || store.list.length === 0) {
        securePost('/inventory/storehouse/get-storehouse', {
          company: companyUid,
          all: true,
          module: config.module
        }).then(res => {
          if (res.status === 200 && res.storehouse) {
            updateStore({ list: res.storehouse })
          }
        }).catch(() => {})
      }

      // 2. Fetch movement detail
      const movementResponse = await securePost('/inventory/movement/get-movement', {
        company: companyUid,
        uidMovement: id,
        module: config.module
      })

      if (movementResponse.status === 200 && movementResponse.movement) {
        const mov = movementResponse.movement
        setMovement(mov)

        // 3. Fetch related notes if movement has document_number
        if (mov.document_number) {
          const notesResponse = await securePost('/inventory/movement/get-notes-by-reference', {
            company: mov.company_uid || companyUid,
            referenceId: mov.document_number,
            module: config.module
          })
          
          if (notesResponse?.status === 200) {
            setNotes(notesResponse.noteList || [])
          }
        }
      } else {
        setError(true)
      }
    } catch (err) {
      console.error("Error fetching movement detail:", err)
      setError(true)
    } finally {
      isFetchingRef.current = false
      setLoading(false)
    }
  }

  useEffect(() => {
    if (id && company.current?.uid) {
      void fetchMovementData()
    }
  }, [id, company.current?.uid])

  // Validación de empresa: el movimiento debe pertenecer a la empresa activa
  useEffect(() => {
    if (movement) {
      validateResourceCompany(
        movement.company_id,
        movement.company_uid || movement.companies?.uid,
        '/movements',
        'movimiento de almacén'
      );
    }
  }, [movement, validateResourceCompany])

  const handleSendEmail = async () => {
    if (!movement?.electronic_invoice_number) {
      return message.error("No hay número de factura electrónica asociado")
    }
    setSendingEmail(true)
    try {
      const res = await securePost('/electronic-invoicing/send-email', {
        company: company.current.uid,
        billNumber: movement.electronic_invoice_number,
        email: emailTarget || undefined,
        module: config.module
      })
      if (res.status === 200) {
        message.success(res.message || "Factura electrónica enviada por correo exitosamente")
        setEmailModalVisible(false)
      } else {
        message.error(res.message || "Error al enviar la factura por correo")
      }
    } catch (err: any) {
      message.error(err?.message || "Error al enviar la factura por correo")
    } finally {
      setSendingEmail(false)
    }
  }

  const handleRetryEmission = async () => {
    setRetrying(true)
    try {
      const res = await securePost('/electronic-invoicing/retry-invoice', {
        company: company.current.uid,
        companyId: company.current.id,
        movementUid: movement?.uid || id,
        module: config.module
      })
      if (res.status === 200) {
        message.success("Factura electrónica emitida y validada exitosamente")
        void fetchMovementData(true)
      } else {
        message.error(res.message || "No fue posible validar la factura ante la DIAN")
        void fetchMovementData(true)
      }
    } catch (err: any) {
      message.error(err?.message || "Error al reintentar emisión")
    } finally {
      setRetrying(false)
    }
  }

  const hasCompanyMismatch = Boolean(
    movement?.company_id &&
    company.current?.id &&
    company.current.id !== 0 &&
    movement.company_id !== company.current.id
  );

  if (loading || isSwitchingCompany || hasCompanyMismatch) {
    const tipText = isSwitchingCompany || hasCompanyMismatch
      ? "Validando empresa del movimiento..."
      : "Cargando movimiento...";
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Spin size="large" tip={tipText} />
        <div style={{ marginTop: 12 }}>{tipText}</div>
      </div>
    );
  }

  if (error || !movement) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Title level={4}>Movimiento no encontrado</Title>
        <Button icon={<RollbackOutlined />} onClick={() => history.goBack()}>Volver</Button>
      </div>
    )
  }

  const getStoreName = (storeId: number | null | undefined) => {
    if (!storeId) return ""
    const storeFound = store.list?.find(el => el.id === storeId)
    return storeFound?.name || ""
  } 

  const storeFrom = getStoreName(movement?.storehouse_from_id)
  const storeTo = getStoreName(movement?.storehouse_to_id)
  let locationInfo = ""
  if (storeFrom && storeTo) locationInfo = `${storeFrom} -> ${storeTo}`
  else if (storeFrom) locationInfo = `Origen: ${storeFrom}`
  else if (storeTo) locationInfo = `Destino: ${storeTo}`

  const beforeTaxes = movement?.total_amount || 0 
  const itemsDiscount = (movement?.item_list || []).reduce((acc: number, current: any) => acc + (Number(current.discount_amount) || 0), 0)
  const discounts = Number(movement?.total_discount_amount ?? itemsDiscount)
  const sumTaxes = movement?.taxes_list?.reduce((acc: number, current: any) => acc + current.tax_amount, 0) || Number(movement?.total_tax_amount || 0)
  const total = beforeTaxes + sumTaxes - discounts
  const totalPaid = Number(movement?.total_paid_amount || 0)
  const pendingBalance = Math.max(0, Math.round(((total - totalPaid) + Number.EPSILON) * 100) / 100)
  const isPaidInFull = (totalPaid >= total && total > 0) || pendingBalance <= 0 || movement?.payment_status === "PAID"
  const taxesData = movement?.taxes_list || []

  const itemListData = movement?.item_list?.map((el: any, i) => {
    const client_amount = el.total_amount_net || el.total_amount_full || el.total_amount || 0
    const client_unit_price = (el.unit_price !== undefined && el.unit_price !== null && !Number.isNaN(Number(el.unit_price)))
      ? Number(el.unit_price)
      : (((Number(el.total_amount) || 0) / (Number(el.quantity) || 1)) || ((client_amount) / (Number(el.quantity) || 1)))
    const total_tax_amount = Number(
      el.total_tax_amount ??
      (el.taxes_list || el.taxes || []).reduce((acc: number, t: any) => acc + (Number(t.tax_amount) || 0), 0) ??
      0
    )
    return {
      ...el,
      register: i + 1,
      id_view: el.custom_id || el.item_code || el.key || el.uid,
      display: el.display || el.presentation || "UND",
      discount_amount: el.discount_amount || 0,
      total_tax_amount,
      presentation: el.presentation || el.display || "UND",
      client_amount,
      client_unit_price
    }
  }) || []

  const docType = String(movement?.document_type || (movement as any)?.type || "").toUpperCase()
  const isPreparatory = ['PURCHASE_ORDER', 'PURCHASE_REQUEST', 'QUOTATION'].includes(docType)
  const hasPendingFulfillment = Boolean(
    !isPreparatory &&
    movement?.reception_status !== 'RECEIVED' &&
    movement?.item_list?.some((el: any) =>
      el.register_type !== 'SERVICE' &&
      el.track_inventory !== false &&
      (Number(el.quantity || 0) - Number(el.quantity_received || 0)) > 0
    )
  )

  const columnsItems: TableColumnsType = [
    { title: "#", dataIndex: "register", width: 50 },
    { title: "ID", dataIndex: "id_view" },
    { title: "Nombre", dataIndex: "name", render: (text, record: any) => {
      const name = record.item_name || text
      const regType = record.register_type
      return (
        <span>
          {name}
          {regType === 'SERVICE' && <Tag color="geekblue" style={{ marginLeft: 6, fontSize: 10 }}>Servicio</Tag>}
          {regType === 'FIXED_ASSET' && <Tag color="purple" style={{ marginLeft: 6, fontSize: 10 }}>Activo Fijo</Tag>}
        </span>
      )
    }},
    { title: "Presentación", dataIndex: "display", render: (text, record: any) => {
      if (record.register_type === 'SERVICE') return <Tag color="default">N/A</Tag>
      return text || record?.presentation || "UND"
    }},
    { title: "Cantidad", dataIndex: "quantity", align: "center" },
    ...(hasPendingFulfillment ? [{
      title: isExitMovement(docType) ? "Despachado" : "Recibido Bodega",
      key: "quantity_received",
      align: "center" as const,
      width: 140,
      render: (_: any, record: any) => {
        if (record.register_type === 'SERVICE' || record.track_inventory === false) {
          return <Tag color="default">N/A</Tag>
        }
        const received = Number(record.quantity_received || 0)
        const totalQty = Number(record.quantity || 0)
        const isComplete = received >= totalQty
        return (
          <Tag color={isComplete ? "success" : received > 0 ? "warning" : "volcano"}>
            {received} / {totalQty} {record.display || record.presentation || "UND"}
          </Tag>
        )
      }
    }] : []),
    { title: "Precio", dataIndex: "client_unit_price", align: "right", render: (text) => textNumberFormater(text) },
    { title: "Descuento", dataIndex: "discount_amount", align: "right", render: (text) => textNumberFormater(text) },
    { title: "Impuesto", dataIndex: "total_tax_amount", align: "right", render: (text) => textNumberFormater(text) },
    { title: "Total", dataIndex: "client_amount", align: "right", render: (text) => textNumberFormater(text) }
  ]

  const columnsNotes: TableColumnsType = [
    { title: "Número", dataIndex: "document_number" },
    { title: "Fecha", dataIndex: "created_at", render: (text) => formatDateTime(text) },
    { title: "Tipo", dataIndex: "document_type", render: (text) => {
      const noteLabels = getDocumentLabels(text)
      return (
        <Tag color={text === 'CREDIT_NOTE' ? 'orange' : 'blue'}>
          {noteLabels.title}
        </Tag>
      )
    }},
    { title: "Total", dataIndex: "total_amount_net", align: "right", render: (text) => textNumberFormater(text) },
    { title: "Acción", key: "action", align: "center", render: (_, record: any) => (
      <Button type="link" onClick={() => history.push(`/movement/${record.uid}`)}>Ver Detalle</Button>
    )}
  ]

  const labels = getDocumentLabels(docType || movement?.document_type)

  const documentData: ReceiptData = {
    receiptId: movement?.document_number || "",
    date: formatDateTime(movement?.document_date || movement?.created_at),
    customer: (movement.contact_document || "") + "-" + (movement.contact_name || "CONSUMIDOR FINAL"),
    subtotal: beforeTaxes,
    total: total,
    totalDiscount: discounts,
    companyName: company.current?.name || "",
    companyAddress: company.current?.address || "Dirección no reportada",
    tax: sumTaxes,
    taxesList: taxesData,
    documentTitle: labels.title,
    counterpartyLabel: labels.counterparty,
    userLabel: labels.user,
    message: docType === 'SALE_INVOICE' ? "Gracias por su compra." : "",
    items: (movement?.item_list || []).map((el) => ({
      product: el.name || el.item_name || "",
      quantity: el.quantity || 0,
      price: (el.total_amount_net || el.total_amount_full || 0) / (el.quantity || 1),
      total: (el.total_amount_net || el.total_amount_full || 0),
      discount: el.discount_amount || 0,
      taxAmount: el.total_tax_amount || 0,
      baseAmount: (el.total_amount || 0)
    })),
    seller: movement?.created_by_user_name || "",
    pdv: locationInfo
  }

  const suggestedStore = movement.storehouse_to_id 
    ? getStoreName(movement.storehouse_to_id) 
    : (movement.storehouse_from_id ? getStoreName(movement.storehouse_from_id) : "Sin asignar")

  return (
    <div style={{ padding: 24 }}>
      <MovementDetailHeaderBar
        movement={movement}
        labels={labels}
        docType={docType}
        isPreparatory={isPreparatory}
        isPaidInFull={isPaidInFull}
        hasPendingFulfillment={hasPendingFulfillment}
        documentData={documentData}
        onGoBack={() => history.goBack()}
        onOpenFulfillModal={() => setFulfillModalVisible(true)}
        onOpenPaymentModal={() => setPaymentModalVisible(true)}
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} md={16} lg={17}>
          <MovementDetailItemsCard
            title={labels.title}
            columnsItems={columnsItems}
            itemListData={itemListData}
            beforeTaxes={beforeTaxes}
            discounts={discounts}
            sumTaxes={sumTaxes}
            total={total}
          />

          <MovementDetailAttachmentsCard
            movementId={movement.id}
            movementUid={movement.uid || id}
            attachments={movement.attachments || []}
            docType={docType || movement.document_type || ""}
            onAttachmentsChange={(newAttachments) => {
              setMovement((prev: any) => ({
                ...prev,
                attachments: newAttachments,
              }))
            }}
          />

          <MovementDetailNotesCard
            notes={notes}
            columnsNotes={columnsNotes}
          />

          <MovementTimelineView
            movementId={movement.id}
            movementUid={movement.uid || id}
            initialEvents={movement.events}
            refreshKey={timelineRefreshKey}
          />
        </Col>

        <Col xs={24} md={8} lg={7}>
          <MovementDetailDebtCard
            movement={movement}
            isPreparatory={isPreparatory}
            total={total}
            pendingBalance={pendingBalance}
            isPaidInFull={isPaidInFull}
            paymentOptions={register?.paymentOptions}
            onOpenPaymentModal={() => setPaymentModalVisible(true)}
          />

          <MovementDetailElectronicInvoiceCard
            movement={movement}
            retrying={retrying}
            onRetryEmission={handleRetryEmission}
            onOpenEmailModal={() => {
              setEmailTarget("")
              setEmailModalVisible(true)
            }}
          />

          <MovementDetailGeneralInfoCard
            movement={movement}
            labels={labels}
            docType={docType}
            isPreparatory={isPreparatory}
            storeFrom={storeFrom}
            storeTo={storeTo}
            suggestedStore={suggestedStore}
          />
        </Col>
      </Row>

      {/* Modal para Reenviar Factura por Correo */}
      <Modal
        title="Reenviar Factura Electrónica por Correo"
        open={emailModalVisible}
        onCancel={() => setEmailModalVisible(false)}
        onOk={handleSendEmail}
        confirmLoading={sendingEmail}
        okText="Enviar Factura"
        cancelText="Cancelar"
      >
        <div style={{ marginBottom: 12 }}>
          <Text>
            Ingrese el correo electrónico al cual desea enviar la representación gráfica y XML de la factura <strong>{movement.electronic_invoice_number}</strong>:
          </Text>
        </div>
        <Input 
          prefix={<MailOutlined />} 
          placeholder="correo@ejemplo.com (Opcional si se registró previamente)" 
          value={emailTarget}
          onChange={(e) => setEmailTarget(e.target.value)}
        />
      </Modal>

      {/* Modal para Entrada Física a Bodega */}
      <FulfillMovementModal
        visible={fulfillModalVisible}
        onClose={() => setFulfillModalVisible(false)}
        movement={movement}
        onSuccess={() => {
          void fetchMovementData(true)
          setTimelineRefreshKey((k) => k + 1)
        }}
      />

      {/* Modal para Registrar Abono / Pago */}
      <RegisterMovementPaymentModal
        visible={paymentModalVisible}
        onClose={() => setPaymentModalVisible(false)}
        movement={movement}
        onSuccess={() => {
          void fetchMovementData(true)
          setTimelineRefreshKey((k) => k + 1)
        }}
      />
    </div>
  )
}

export default ViewMovementDetail
